import * as Yup from 'yup';
import { DeleteIcon } from '@synergy/theme-next';
import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import {
    PoolButton,
    PoolConfirmationModal,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTextfield,
} from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { Stack } from '@mui/material';
import { fields } from '@synergy/utils';

import { WishListListItem } from '../../../models/wish-list-list-item';
import { createWishList, deleteWishList, updateWishList } from '../../../store/whish-list-actions';
import { useAppDispatch } from '../../../../store/store';

interface FormValues {
    name: string;
    infoText: string;
}

const fieldNames = fields<FormValues>();

export interface WishListModalProps {
    wishList?: WishListListItem | null;
    open: boolean;
    onClose: () => void;
}

export const WishListModal = (props: WishListModalProps): ReactElement => {
    const { wishList, onClose, open } = props;
    const dispatch = useAppDispatch();
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>): void => {
        if (wishList?.id) {
            dispatch(updateWishList(+wishList.id, values.name));
        } else {
            dispatch(createWishList(values.name));
        }

        handleClose(formikHelpers);
    };

    const handleDeleteConfirm = (formikProps: FormikProps<FormValues>): void => {
        if (wishList && +wishList.id) {
            dispatch(deleteWishList(+wishList.id));
        }
        setShowConfirmation(false);
        handleClose(formikProps);
    };

    const handleClose = (formikHelpers: FormikHelpers<FormValues>): void => {
        setTimeout(() => formikHelpers.resetForm(), 300);
        onClose();
    };

    const initialValues: FormValues = {
        name: wishList?.name ?? '',
        infoText: wishList?.infoText ?? '',
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(),
        infoText: Yup.string().nullable(),
    });

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize={true}
        >
            {formikProps => (
                <PoolModal open={open} onClose={() => handleClose(formikProps)}>
                    <PoolModalTitle>
                        <FormattedMessage id={wishList?.id ? 'app.wishList.edit' : 'app.wishList.add'} />
                    </PoolModalTitle>
                    <PoolModalContent>
                        <Stack gap={2}>
                            <Field name={fieldNames.name}>
                                {({ field }: FieldProps<string>) => (
                                    <PoolTextfield
                                        fullWidth={true}
                                        required={true}
                                        label={<FormattedMessage id="app.wishList.add.label" />}
                                        {...field}
                                        autoFocus={true}
                                    />
                                )}
                            </Field>
                        </Stack>
                    </PoolModalContent>
                    <PoolModalActions>
                        {wishList?.id ? (
                            <PoolButton
                                variant="outlined"
                                onClick={() => setShowConfirmation(true)}
                                startIcon={<DeleteIcon />}
                            >
                                <FormattedMessage id="app.wishList.listDelete" />
                            </PoolButton>
                        ) : (
                            <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                                <FormattedMessage id="app.common.cancel" />
                            </PoolButton>
                        )}
                        <PoolButton onClick={() => formikProps.handleSubmit()}>
                            <FormattedMessage id="app.common.save" />
                        </PoolButton>
                    </PoolModalActions>

                    <PoolConfirmationModal
                        open={showConfirmation}
                        onCancel={() => setShowConfirmation(false)}
                        onConfirm={() => handleDeleteConfirm(formikProps)}
                        action={<FormattedMessage id="app.common.delete" />}
                        title={<FormattedMessage id="app.wishList.listDelete" />}
                    >
                        <PoolFormattedMessage
                            id="app.wishList.listDelete.content"
                            values={{
                                name: wishList?.name ?? '',
                            }}
                        />
                    </PoolConfirmationModal>
                </PoolModal>
            )}
        </Formik>
    );
};
